<template>
  <section class="flex flex-col md:flex-row w-full">
    <!-- LEFT -->
    <div class="hidden md:block w-96 flex-shrink-0">
      <div class="sidecard-base">
        <div class="pt-2">
          <h1 class="text-2xl font-semibold text-primaryPalette">Upcoming Events</h1>
        </div>
        <div class="py-4">
          <ul v-if="passSectionItemCalendarPost.length">
            <li
              v-for="(item, index) in upcomingEvents"
              :key="index"
              class="bg-white shadow-sm my-4 p-5 space-y-2 text-primarylightdark"
            >
              <div class="flex items-center gap-2">
                <FlagSvg />
                <div class="text-sm font-semibold">
                  <span class="text-primaryPaletteLight bg-orange-50 p-1 rounded-md">{{
                    item.event_name
                  }}</span>
                </div>
              </div>
              <div class="flex items-center gap-2">
                <CalendarSvg />
                <span class="text-sm font-normal">{{
                  renderDateFormat(item.date_intended_from)
                }}</span>
              </div>
              <div class="flex items-center gap-2">
                <ClockSvg />
                <span class="text-sm font-normal">{{ renderTimeFormat(item.time_intended) }}</span>
              </div>
              <div class="flex items-center gap-2">
                <MapSvg />
                <span class="text-sm font-normal">{{ item.venue }}</span>
              </div>
            </li>
          </ul>
          <div
            v-else
            class="text-primarylightdark text-sm flex flex-col items-center justify-center py-1 space-y-3"
          >
            <ArchiveSvg />
            <h2>There are currently no celebrations</h2>
          </div>
        </div>
      </div>
    </div>

    <!-- RIGHT -->
    <div class="flex-1 md:pl-6">
      <div class="relative -top-4 sm:-top-8 inset-x-0 z-10 w-full">
        <!-- HEADER -->
        <div
          class="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6 gap-2 sm:gap-0"
        >
          <h2 class="text-lg sm:text-2xl font-bold text-primarydark">
            <template v-if="view === 'month'"
              >{{ monthNames[currentMonth] }} {{ currentYear }}</template
            >
            <template v-else-if="view === 'day'">{{ renderDateFormat(selectedDate) }}</template>
            <template v-else>{{ selectedWeek }}</template>
          </h2>

          <div class="flex flex-wrap gap-1 sm:gap-2">
            <button
              @click="view = 'month'"
              :class="tabClass('month')"
              class="px-2 sm:px-4 py-1 sm:py-2 text-xs sm:text-sm border rounded"
            >
              Month
            </button>
            <button
              @click="view = 'week'"
              :class="tabClass('week')"
              class="px-2 sm:px-4 py-1 sm:py-2 text-xs sm:text-sm border rounded"
            >
              Week
            </button>
            <button
              @click="view = 'day'"
              :class="tabClass('day')"
              class="px-2 sm:px-4 py-1 sm:py-2 text-xs sm:text-sm border rounded"
            >
              Day
            </button>
            <button
              @click="prev"
              class="px-2 sm:px-4 py-1 sm:py-2 text-xs sm:text-sm border rounded"
            >
              &lt;
            </button>
            <button
              @click="next"
              class="px-2 sm:px-4 py-1 sm:py-2 text-xs sm:text-sm border rounded"
            >
              &gt;
            </button>
          </div>
        </div>

        <!-- MOBILE WEEK VIEW -->
        <div v-if="isMobile" class="overflow-x-auto mb-4">
          <div class="flex gap-2 w-max min-w-full">
            <div
              v-for="(d, idx) in weekDates"
              :key="'mob-' + idx"
              class="bg-white border rounded p-3 min-w-[120px] cursor-pointer flex-shrink-0"
              :class="{ 'bg-gray-200': isSameDate(d, selectedDate) }"
              @click="
                selectDate(d);
                view = 'day';
              "
            >
              <div class="text-xs font-medium text-gray-600 mb-1">
                {{ weekDays[d.getDay()] }} {{ d.getDate() }}
              </div>

              <div
                v-for="(ev, eIndex) in calendarEventForDate(d)"
                :key="'mobe-' + eIndex"
                class="bg-primaryPaletteLight text-white rounded px-2 py-1 text-xxs mb-1 truncate"
              >
                {{ ev.event_name }}
              </div>

              <div
                v-if="calendarEventForDate(d).length === 0"
                class="text-gray-400 italic text-xxs"
              >
                No events
              </div>
            </div>
          </div>
        </div>

        <!-- DESKTOP MONTH VIEW -->
        <!-- MOBILE RESPONSIVE MONTH VIEW -->
        <div v-if="view === 'month'" class="w-full">
          <!-- Desktop Grid -->
          <div
            v-if="!isMobile"
            class="grid grid-cols-7 border-t border-l text-sm text-center bg-white"
          >
            <div
              v-for="(day, index) in weekDays"
              :key="'hd-' + index"
              class="border-r border-b py-2 font-medium text-gray-500"
            >
              {{ day }}
            </div>

            <template v-for="(date, i) in monthDays" :key="'md-' + i">
              <div
                class="h-28 border-r border-b p-2 relative cursor-pointer"
                :class="{
                  'bg-gray-50': date && date.getMonth() !== currentMonth,
                  'bg-gray-200': date && isSameDate(date, selectedDate),
                  'hover:bg-indigo-50': date
                }"
                @click="date && selectDate(date)"
              >
                <div
                  class="text-xs font-medium text-gray-600"
                  @click.stop="
                    selectDate(date);
                    view = 'day';
                  "
                >
                  {{ date ? date.getDate() : '' }}
                </div>

                <div
                  v-if="calendarEventForDate(date).length"
                  class="absolute left-2 right-2 top-7 flex flex-col gap-1 text-xs"
                >
                  <div
                    v-for="(item, index) in calendarEventForDate(date)"
                    :key="index"
                    :class="['px-2 py-1 rounded', getColorByIndex(index)]"
                  >
                    {{ item.event_name }}
                  </div>
                </div>
              </div>
            </template>
          </div>

          <!-- Mobile Swipeable Month -->
          <div v-else class="overflow-x-auto pb-2">
            <div class="flex gap-1 w-max">
              <div
                v-for="(date, i) in monthDays"
                :key="'mobmd-' + i"
                class="flex-shrink-0 w-20 h-28 border rounded p-1 relative cursor-pointer"
                :class="{
                  'bg-gray-50': date && date.getMonth() !== currentMonth,
                  'bg-gray-200': date && isSameDate(date, selectedDate),
                  'hover:bg-indigo-50': date
                }"
                @click="date && selectDate(date)"
              >
                <div class="text-xs font-medium text-gray-600 mb-1">
                  {{ date ? date.getDate() : '' }}
                </div>

                <div v-if="calendarEventForDate(date).length" class="flex flex-col gap-1 text-xxs">
                  <div
                    v-for="(item, index) in calendarEventForDate(date)"
                    :key="index"
                    :class="['px-1 py-0.5 rounded truncate', getColorByIndex(index)]"
                  >
                    {{ item.event_name }}
                  </div>
                </div>

                <div v-else class="text-gray-400 italic text-xxs mt-1">No events</div>
              </div>
            </div>
          </div>
        </div>

        <!-- WEEK VIEW -->
        <div v-else-if="view === 'week'" class="mt-4 grid grid-cols-2 sm:grid-cols-7 gap-2">
          <div
            v-for="(d, idx) in weekDates"
            :key="'w-' + idx"
            class="bg-white border rounded p-3 min-h-[160px]"
          >
            <div class="text-sm font-medium mb-2">
              {{ getWeekFrom(d, idx, weekDates) }} {{ weekDays[d.getDay()] }} {{ d.getDate() }}
            </div>

            <div v-if="calendarEventForDate(d).length === 0" class="text-xs text-gray-400 italic">
              No calendar event
            </div>

            <div
              v-for="(ev, eIndex) in calendarEventForDate(d)"
              :key="'wev-' + eIndex"
              class="bg-white shadow rounded p-2 text-xs mb-2"
            >
              <div class="font-medium">{{ ev.event_name }}</div>
            </div>
          </div>
        </div>

        <!-- DAY VIEW -->
        <div v-else class="mt-4 space-y-3">
          <div
            v-for="(ev, idx) in calendarEventForDate(selectedDate)"
            :key="'dev-' + idx"
            class="bg-white rounded shadow p-4"
          >
            <div class="flex flex-col justify-center gap-3">
              <div class="flex items-center gap-2">
                <FlagSvg />
                <div class="text-lg sm:text-xl font-semibold">
                  <span class="text-primaryPaletteLight bg-orange-50 p-1 rounded-md truncate">{{
                    ev.event_name
                  }}</span>
                </div>
              </div>

              <div class="flex items-center gap-2">
                <CalendarSvg />
                <span class="text-sm font-normal">{{
                  renderDaysInterval(ev.date_intended_from)
                }}</span>
              </div>
              <div class="flex items-center gap-2">
                <ClockSvg />
                <span class="text-sm font-normal">{{ renderTimeFormat(ev.time_intended) }}</span>
              </div>
              <div class="flex items-center gap-2">
                <MapSvg />
                <span class="text-sm font-normal">{{ ev.venue }}</span>
              </div>
            </div>
          </div>

          <div v-if="calendarEventForDate(selectedDate).length === 0" class="text-gray-500 italic">
            No calendar event for this day.
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { ref, reactive, computed, onMounted, onBeforeUnmount, watch, inject } from 'vue';
import FlagSvg from '@/components/svgs/FlagSvg.vue';
import CalendarSvg from '@/components/svgs/CalendarSvg.vue';
import ClockSvg from '@/components/svgs/ClockSvg.vue';
import MapSvg from '@/components/svgs/MapSvg.vue';
import reusableBase from '@/utils/reusables/dateFormat';
export default {
  name: 'CalendarEvent',
  components: {
    FlagSvg,
    CalendarSvg,
    ClockSvg,
    MapSvg
  },
  props: {
    passSectionItemPost: Object,
    passSectionItemCalendarPost: Object
  },
  setup(props) {
    const { renderDateFormat, renderTimeFormat, renderDaysInterval } = reusableBase();
    const moment = inject('$moment');
    const today = new Date();
    const view = ref('month');
    const currentYear = ref(today.getFullYear());
    const currentMonth = ref(today.getMonth());
    const selectedDate = ref(new Date());
    const selectedWeek = ref();
    const renderCurrentLoading = ref(false);
    const colors = ref([
      'bg-green-100 text-green-800',
      'bg-blue-100 text-blue-800',
      'bg-red-100 text-red-800',
      'bg-yellow-100 text-yellow-800',
      'bg-purple-100 text-purple-800',
      'bg-pink-100 text-pink-800'
    ]);

    const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fr', 'Sat'];
    const monthNames = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December'
    ];

    const calendarEvent = reactive(props.passSectionItemCalendarPost || []);
    const isMobile = ref(false);

    const upcomingEvents = computed(() => {
      if (!props.passSectionItemCalendarPost?.length) return [];

      const today = moment().startOf('day');
      const endDate = moment().add(6, 'days').endOf('day');

      return props.passSectionItemCalendarPost
        .filter((event) => moment(event.date_intended_from).isBetween(today, endDate, null, '[]'))
        .sort((a, b) => moment(a.date_intended_from).diff(moment(b.date_intended_from)));
    });
    const monthDays = computed(() => {
      const days = [];
      const firstDay = new Date(currentYear.value, currentMonth.value, 1);
      const lastDay = new Date(currentYear.value, currentMonth.value + 1, 0);

      for (let i = 0; i < firstDay.getDay(); i++) days.push(null);
      for (let i = 1; i <= lastDay.getDate(); i++) {
        days.push(new Date(currentYear.value, currentMonth.value, i));
      }
      while (days.length % 7 !== 0) days.push(null);

      return days;
    });

    const weekDates = computed(() => {
      const start = new Date(selectedDate.value);
      start.setDate(start.getDate() - start.getDay());

      return Array.from({ length: 7 }, (_, i) => {
        const d = new Date(start);
        d.setDate(start.getDate() + i);
        return d;
      });
    });

    const formatKey = (date) => {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    };

    const calendarEventForDate = (date) => {
      if (!date) return [];
      const key = formatKey(date);
      return calendarEvent.filter((e) => e.date_intended_from === key);
    };

    const selectDate = (date) => {
      selectedDate.value = new Date(date);
      currentMonth.value = date.getMonth();
      currentYear.value = date.getFullYear();
    };

    const isSameDate = (a, b) =>
      a &&
      b &&
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate();

    const tabClass = (name) =>
      view.value === name
        ? 'bg-primarydark text-white font-semibold'
        : 'text-gray-600 hover:text-primaryFontActive';

    // ✅ FIXED PREV / NEXT
    const prev = () => {
      if (view.value === 'month') {
        currentMonth.value === 0
          ? ((currentMonth.value = 11), currentYear.value--)
          : currentMonth.value--;
      } else {
        const d = new Date(selectedDate.value);
        d.setDate(d.getDate() - (view.value === 'week' ? 7 : 1));
        selectedDate.value = d;
      }
    };

    const next = () => {
      if (view.value === 'month') {
        currentMonth.value === 11
          ? ((currentMonth.value = 0), currentYear.value++)
          : currentMonth.value++;
      } else {
        const d = new Date(selectedDate.value);
        d.setDate(d.getDate() + (view.value === 'week' ? 7 : 1));
        selectedDate.value = d;
      }
    };

    // keep header synced in week/day
    watch(selectedDate, (d) => {
      currentMonth.value = d.getMonth();
      currentYear.value = d.getFullYear();
    });

    /*const checkMobile = () => {
      isMobile.value = window.innerWidth < 640;
      if (isMobile.value && view.value === 'month') view.value = 'week';
    };*/

    onMounted(() => {
      //checkMobile();
      //window.addEventListener('resize', checkMobile);
    });

    onBeforeUnmount(() => {
      //window.removeEventListener('resize', checkMobile);
    });

    const getWeekFrom = (val, index, arr) => {
      if (index !== 0) return;

      const first = moment(arr[0]);
      const last = moment(arr[arr.length - 1]);

      selectedWeek.value = `${first.format('MMMM DD')} - ${last.format('MMMM DD, YYYY')}`;
    };

    const getColorByIndex = (index) => {
      return colors.value[index % colors.value.length];
    };

    return {
      view,
      currentYear,
      currentMonth,
      selectedDate,
      weekDays,
      monthNames,
      calendarEvent,
      isMobile,
      monthDays,
      weekDates,
      tabClass,
      calendarEventForDate,
      selectDate,
      isSameDate,
      prev,
      next,
      renderCurrentLoading,
      upcomingEvents,
      renderDateFormat,
      renderTimeFormat,
      getWeekFrom,
      selectedWeek,
      renderDaysInterval,
      colors,
      getColorByIndex
    };
  }
};
</script>
